import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './dash.css';
import useAdmissionJourney from '../Components/useAdmissionJourney';
import useInterviewMenuAccess from '../Components/useInterviewMenuAccess';
import {
  DEFAULT_USD_TO_KES_RATE,
  fetchUsdToKesRate,
  formatMoneyFromKes,
  resolveStudentCurrency,
} from '../utils/currency';

const DEFAULT_ADMISSION_FEE_AMOUNT_KES = 2000;
const ADMISSION_FEE_SETTINGS_DOC = 'admissionFee';

function AdmissionStepsComp() {
  const navigate = useNavigate();
  const hasInterviewMenuAccess = useInterviewMenuAccess();
  const {
    isLoading,
    studentRecord,
    isAdmitted,
    isAdmissionFeePaid,
    isAdmissionFeeExempt,
    canContinueTuitionWithoutAdmissionFee: canContinueTuition,
    hasProspectAdmissionNumber,
    prospectAdmissionNumber,
  } = useAdmissionJourney();
  const [usdToKesRate, setUsdToKesRate] = useState(DEFAULT_USD_TO_KES_RATE);
  const [admissionFeeAmountKes, setAdmissionFeeAmountKes] = useState(DEFAULT_ADMISSION_FEE_AMOUNT_KES);

  useEffect(() => {
    let isMounted = true;

    const fetchFinanceSettings = async () => {
      try {
        const [rate, admissionFeeSnap] = await Promise.all([
          fetchUsdToKesRate(db),
          getDoc(doc(db, 'Settings', ADMISSION_FEE_SETTINGS_DOC)),
        ]);
        const configuredAmount = Number(admissionFeeSnap.data()?.amountKes || DEFAULT_ADMISSION_FEE_AMOUNT_KES);

        if (isMounted) {
          setUsdToKesRate(rate);
          setAdmissionFeeAmountKes(
            Number.isFinite(configuredAmount) && configuredAmount > 0
              ? configuredAmount
              : DEFAULT_ADMISSION_FEE_AMOUNT_KES
          );
        }
      } catch (error) {
        console.error('Failed to load admission fee settings:', error);
      }
    };

    fetchFinanceSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const studentCurrencyCode = resolveStudentCurrency(studentRecord) === 'USD' ? 'USD' : 'KES';
  const admissionFeeDisplayAmount = formatMoneyFromKes(admissionFeeAmountKes, studentCurrencyCode, usdToKesRate);
  const admissionFeeAmountPaidKes = Math.max(0, Number(studentRecord.admissionFeeAmountPaid || 0));
  const storedAdmissionFeeBalance = Number(studentRecord.admissionFeeBalance);
  const admissionFeeBalanceKes = Math.max(
    0,
    Number.isFinite(storedAdmissionFeeBalance)
      ? storedAdmissionFeeBalance
      : Math.max(0, admissionFeeAmountKes - admissionFeeAmountPaidKes)
  );
  const admissionFeePaidDisplayAmount = formatMoneyFromKes(admissionFeeAmountPaidKes, studentCurrencyCode, usdToKesRate);
  const admissionFeeBalanceDisplayAmount = formatMoneyFromKes(admissionFeeBalanceKes, studentCurrencyCode, usdToKesRate);
  const hasPartialAdmissionFeePayment = !isAdmissionFeePaid && admissionFeeAmountPaidKes > 0;
  const accountStatusLabel = studentRecord.userStatus || studentRecord.Status || 'Continuing';

  const heroTitle = isAdmissionFeeExempt && !isAdmissionFeePaid && !isAdmitted
    ? 'Your account can continue with tuition payments'
    : isAdmitted
    ? 'Your admission is complete'
    : isAdmissionFeePaid
      ? 'Your admission is in progress'
      : hasProspectAdmissionNumber
        ? 'Your next admission step is ready'
        : 'Your application is under review';

  const heroDescription = isAdmissionFeeExempt && !isAdmissionFeePaid && !isAdmitted
    ? `This account is currently marked as ${accountStatusLabel}. We will not require an admission-fee payment here, and you can continue with tuition payments from your fee invoices page.`
    : isAdmitted
    ? 'You have completed the admissions journey and your student account is active.'
    : isAdmissionFeePaid
      ? 'We have confirmed your admission fee. Our admissions team will contact you with interview scheduling and the next enrollment steps.'
      : hasProspectAdmissionNumber
        ? 'Your application reference is ready. Complete the admission fee so our admissions team can move your application forward to interview planning.'
        : 'Our admissions team is reviewing your application and preparing your admission reference. Please check again shortly.';

  const primaryAction = useMemo(() => {
    if (isAdmissionFeeExempt && !isAdmissionFeePaid && !isAdmitted) {
      return {
        label: 'Open Fee Invoices',
        onClick: () => navigate('/fee-invoices'),
        disabled: false,
      };
    }

    if (isAdmitted) {
      return {
        label: 'Open Dashboard',
        onClick: () => navigate('/dashboard'),
        disabled: false,
      };
    }

    if (isAdmissionFeePaid) {
      if (hasInterviewMenuAccess) {
        return {
          label: 'Open Interviews',
          onClick: () => navigate('/interviews'),
          disabled: false,
        };
      }

      return {
        label: 'Back to Dashboard',
        onClick: () => navigate('/dashboard'),
        disabled: false,
      };
    }

    if (hasProspectAdmissionNumber) {
      return {
        label: 'Go to Admission Payment',
        onClick: () => navigate('/fee-invoices', { state: { focusAdmissionFee: true } }),
        disabled: false,
      };
    }

    return {
      label: 'Reference in Progress',
      onClick: null,
      disabled: true,
    };
  }, [hasInterviewMenuAccess, hasProspectAdmissionNumber, isAdmissionFeeExempt, isAdmitted, isAdmissionFeePaid, navigate]);

  const statusCards = [
    {
      label: 'Application',
      value: isAdmitted ? 'Admitted' : (isAdmissionFeeExempt ? accountStatusLabel : (studentRecord.applicationStage || 'Application Received')),
      note: studentRecord.course || 'Your selected course',
    },
    {
      label: 'Application Ref',
      value: prospectAdmissionNumber || 'Preparing',
      note: prospectAdmissionNumber ? 'Use this reference for your admission fee.' : 'Your admissions reference will appear here once ready.',
    },
    {
      label: 'Admission Fee',
      value: isAdmissionFeePaid ? 'Paid' : (isAdmissionFeeExempt ? 'Not required' : admissionFeeBalanceDisplayAmount),
      note: isAdmissionFeePaid
        ? `Payment has been received and recorded. Total paid: ${admissionFeePaidDisplayAmount}.`
        : isAdmissionFeeExempt
          ? 'This account can continue with tuition payments without a recorded admission fee.'
          : hasPartialAdmissionFeePayment
            ? `Paid so far: ${admissionFeePaidDisplayAmount}. Remaining balance: ${admissionFeeBalanceDisplayAmount}.`
            : 'This fee is required before interview planning.',
    },
  ];

  const journeySteps = [
    {
      key: 'application',
      title: 'Application received',
      detail: 'Your application has been submitted to our admissions team.',
      status: 'done',
    },
    {
      key: 'reference',
      title: 'Admission reference',
      detail: hasProspectAdmissionNumber
        ? `Ready: ${prospectAdmissionNumber}`
        : 'Our admissions team is preparing your application reference.',
      status: hasProspectAdmissionNumber ? 'done' : 'active',
    },
    {
      key: 'payment',
      title: 'Admission fee',
      detail: isAdmissionFeePaid
        ? 'Payment confirmed and recorded on your account.'
        : isAdmissionFeeExempt
          ? 'This account is exempt from the admission-fee prompt and can continue with tuition payments.'
        : hasProspectAdmissionNumber
          ? hasPartialAdmissionFeePayment
            ? `Paid so far: ${admissionFeePaidDisplayAmount}. Remaining balance: ${admissionFeeBalanceDisplayAmount}.`
            : `Pending payment of ${admissionFeeDisplayAmount}.`
          : 'Available once your admission reference is ready.',
      status: isAdmissionFeePaid ? 'done' : isAdmissionFeeExempt ? 'done' : hasProspectAdmissionNumber ? 'active' : 'upcoming',
    },
    {
      key: 'interview',
      title: 'Interview scheduling',
      detail: isAdmitted
        ? 'Your account is active and this step has been completed.'
        : isAdmissionFeeExempt
          ? 'This account has already moved beyond the admission-fee stage.'
        : isAdmissionFeePaid
          ? 'Our admissions team will contact you with your interview details.'
          : 'This step follows after the admission fee is confirmed.',
      status: isAdmitted ? 'done' : isAdmissionFeeExempt ? 'done' : isAdmissionFeePaid ? 'active' : 'upcoming',
    },
  ];

  if (isLoading) {
    return (
      <div className='admissionJourneyPage'>
        <div className='admissionJourneyLoading'>
          <Spinner animation='border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </Spinner>
          <p>Loading your admission steps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='admissionJourneyPage'>
      <section className='admissionJourneyHero'>
        <div className='admissionJourneyHeroCopy'>
          <span className='admissionJourneyEyebrow'>Admission steps</span>
          <h4>{heroTitle}</h4>
          <p>{heroDescription}</p>
        </div>
        <div className='admissionJourneyHeroActions'>
          <button
            type='button'
            className='admissionJourneyPrimaryBtn'
            onClick={primaryAction.onClick || undefined}
            disabled={primaryAction.disabled}
          >
            {primaryAction.label}
          </button>
        </div>
      </section>

      <section className='admissionJourneyStats'>
        {statusCards.map((card) => (
          <article key={card.label} className='admissionJourneyStatCard'>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
            <p>{card.note}</p>
          </article>
        ))}
      </section>

      <section className='admissionJourneyChecklistCard'>
        <div className='admissionJourneySectionHeader'>
          <div>
            <span className='admissionJourneySectionEyebrow'>Your next steps</span>
            <h5>Track your admission progress</h5>
          </div>
          {!isAdmitted && !canContinueTuition && (
            <p>
              We will keep this page updated as your application moves through each stage.
            </p>
          )}
        </div>

        <div className='admissionJourneyStepList'>
          {journeySteps.map((step, index) => (
            <div key={step.key} className={`admissionJourneyStep ${step.status}`}>
              <div className='admissionJourneyStepMarker'>
                {step.status === 'done' ? '✓' : index + 1}
              </div>
              <div className='admissionJourneyStepCopy'>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default AdmissionStepsComp;
